// Question content with its placeholders made real. The tutor's copy of the student app's
// component of the same name - same markup, same classes, different service import, and no
// answers to fill gaps with.
//
// LCMS authors content in a rich-text editor that stores some things as empty custom spans. They
// carry all their meaning in data attributes, so a browser renders them as NOTHING - which is
// how a listening question arrived silent in this preview and a cloze question arrived with its
// gaps missing from the sentence.
//
// LCMS stores a clip as a placeholder inside the content HTML:
//
//   <span data-type="audio-node" data-audio-id="..." data-file-name="..." data-label="..."></span>
//
// The bytes are in the database rather than on a CDN and the endpoint needs the tutor's token,
// so a plain <audio src> cannot reach them either.
//
// This renders the content as HTML, then finds those placeholders and turns each into a play
// button that fetches the clip on first press and keeps it for later presses.

import React, { useCallback, useEffect, useRef } from "react"
import { fetchEngDiagAudio } from "../services/eng-diag.service"
import styles from "./eng-diag-question-view.module.css"

interface EngDiagRichContentProps {
  html: string
  className?: string
}

/** Label under the button. The author's own label wins; otherwise something readable. */
function buttonLabel(node: HTMLElement): string {
  return (node.dataset.label || "").trim() || "Listen"
}

export const EngDiagRichContent: React.FC<EngDiagRichContentProps> = ({ html, className }) => {
  const hostRef = useRef<HTMLDivElement | null>(null)
  // Object URLs per clip, so pressing play twice does not fetch twice. Revoked on unmount.
  const urlsRef = useRef<Map<string, string>>(new Map())
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const play = useCallback(async (audioId: string, button: HTMLButtonElement) => {
    const existing = audioRef.current
    // One clip at a time: two talking at once is unusable.
    if (existing) {
      existing.pause()
      existing.currentTime = 0
    }

    try {
      let url = urlsRef.current.get(audioId)
      if (!url) {
        button.disabled = true
        button.dataset.state = "loading"
        const blob = await fetchEngDiagAudio(audioId)
        url = URL.createObjectURL(blob)
        urlsRef.current.set(audioId, url)
      }

      const audio = new Audio(url)
      audioRef.current = audio
      button.dataset.state = "playing"
      audio.onended = () => {
        button.dataset.state = "idle"
      }
      audio.onerror = () => {
        button.dataset.state = "error"
      }
      await audio.play()
    } catch {
      // Marks the button rather than interrupting the preview. They can press again.
      button.dataset.state = "error"
    } finally {
      button.disabled = false
    }
  }, [])

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const placeholders = Array.from(
      host.querySelectorAll<HTMLElement>('[data-type="audio-node"]'),
    )

    const cleanups: Array<() => void> = []
    for (const node of placeholders) {
      const audioId = (node.dataset.audioId || "").trim()
      if (!audioId) continue

      const button = document.createElement("button")
      button.type = "button"
      button.className = styles.engDiagAudioButton
      button.dataset.state = "idle"
      // Spoken by a screen reader, and shown if the icon font fails.
      button.setAttribute("aria-label", `Play audio: ${buttonLabel(node)}`)
      button.innerHTML =
        `<span aria-hidden="true">&#9654;</span><span>${buttonLabel(node)}</span>`

      const onClick = (event: Event) => {
        event.preventDefault()
        // A clip can sit INSIDE a multiple-choice option, which is itself clickable in the
        // preview. Without this, pressing play would also pick that option.
        event.stopPropagation()
        void play(audioId, button)
      }
      button.addEventListener("click", onClick)
      cleanups.push(() => button.removeEventListener("click", onClick))

      node.replaceChildren(button)
    }

    return () => cleanups.forEach(fn => fn())
  }, [html, play])

  // A tutor is looking at the question, not answering it, so every gap is empty - but it still
  // has to occupy space, or the sentence closes up and there is no sign anything is missing.
  // Sized by the author's character count, the same as in the student app.
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    for (const gap of Array.from(
      host.querySelectorAll<HTMLElement>(
        '[data-type="mc-cloze-blank"], [data-type="answer-placeholder"]',
      ),
    )) {
      gap.className = styles.engDiagClozeGap
      const width = Number(gap.dataset.charCount) || 6
      gap.style.minWidth = `${Math.max(3, width)}ch`
      gap.textContent = ""
    }
  }, [html])

  useEffect(() => {
    const urls = urlsRef.current
    return () => {
      audioRef.current?.pause()
      urls.forEach(url => URL.revokeObjectURL(url))
      urls.clear()
    }
  }, [])

  return (
    <div
      ref={hostRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
